import logger from '../utils/logger';
import React, { useState, useRef, useEffect } from 'react';
import { usePostsTranslation } from '../hooks/usePostsTranslation';
import { X, Image, MapPin, Hash, Upload, Trash2 } from 'lucide-react';
import fileUploadService from '../services/fileUploadService';
import toast from 'react-hot-toast';

const AdvancedPostModal = ({ isOpen, onClose, onSubmit, loading, editingPost = null }) => {
  const { t } = usePostsTranslation();
  const [content, setContent] = useState('');
  const [location, setLocation] = useState('');
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingPost) {
      setContent(editingPost.content || '');
      setLocation(editingPost.location || '');
      setCategory(editingPost.category || '');
      setPrice(editingPost.price || '');
      setIsFeatured(editingPost.isFeatured || false);
      // Load existing images as URLs (not file objects)
      const existingImages = (editingPost.images || []).map((url, index) => ({
        id: `existing_${index}`,
        url: url,
        isExisting: true,
        preview: url
      }));
      setImages(existingImages);
    } else {
      setContent('');
      setLocation('');
      setCategory('');
      setPrice('');
      setIsFeatured(false);
      setImages([]);
    }
  }, [editingPost, isOpen]);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      const validation = fileUploadService.validateFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    });
    
    const validFiles = files.filter(file => {
      const validation = fileUploadService.validateFile(file);
      return validation.valid;
    });
    
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: `new_${Date.now()}_${Math.random()}`,
      isExisting: false
    }));
    
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

  logger.debug('=== POST SUBMISSION DEBUG ===');
  logger.debug('Editing post:', editingPost ? 'Yes' : 'No');
  logger.debug('Images array:', images);
  logger.debug('Images count:', images.length);

    const finalImageUrls = [];
    
    for (const image of images) {
  logger.debug('Processing image:', image);
      
      if (image.isExisting && image.url) {
        // Keep existing image URLs as-is
  logger.debug('Keeping existing image URL:', image.url);
        finalImageUrls.push(image.url);
      } else if (image.file) {
        // Upload new images
  logger.debug('Uploading new file:', image.file.name);
        const uploadResult = await fileUploadService.uploadPostImage(image.file);
  logger.debug('Upload result:', uploadResult);
        if (uploadResult.success) {
          logger.debug('Upload successful, URL:', uploadResult.data.url);
          finalImageUrls.push(uploadResult.data.url);
        } else {
          logger.error('Upload failed:', uploadResult.error);
          toast.error(`Failed to upload ${image.file.name}`);
        }
      } else {
  logger.debug('Skipping invalid image:', image);
      }
    }

  logger.debug('Final image URLs:', finalImageUrls);

    const postData = {
      content: content,
      location: location || '',
      images: finalImageUrls,
      category: category || null,
      price: price || null,
      isFeatured: isFeatured
    };

  logger.debug('Sending post data:', postData);
    await onSubmit(postData);
    handleClose();
  };

  const handleClose = () => {
    setContent('');
    setLocation('');
    setCategory('');
    setPrice('');
    setIsFeatured(false);
    setImages([]);
    onClose();
  };

  const insertHashtag = () => {
    const hashtag = prompt(t('posts.enterHashtag'));
    if (hashtag) {
      setContent(prev => prev + ` #${hashtag}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1f2937] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-600">
          <h2 className="text-2xl font-bold text-white">
            {editingPost ? t('posts.editPost') : t('posts.createPost')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('posts.showcaseLabel')}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('posts.showcasePlaceholder')}
              className="w-full h-32 p-4 bg-gray-800 border border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-[#a15507] focus:border-transparent text-white placeholder-gray-400"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-400">{content.length}/500</span>
              <button
                onClick={insertHashtag}
                className="flex items-center space-x-1 text-[#a15507] hover:text-[#924e06] text-sm font-medium"
              >
                <Hash className="w-4 h-4" />
                <span>{t('posts.addHashtag')}</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('posts.location')}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={t('posts.locationPlaceholder')}
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#a15507] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('posts.category')}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#a15507] focus:border-transparent text-white"
              >
                <option value="">{t('posts.selectCategory')}</option>
                <option value="Coffee">{t('posts.categories.coffee')}</option>
                <option value="Pastries">{t('posts.categories.pastries')}</option>
                <option value="Sandwiches">{t('posts.categories.sandwiches')}</option>
                <option value="Specials">{t('posts.categories.specials')}</option>
                <option value="Beverages">{t('posts.categories.beverages')}</option>
                <option value="Desserts">{t('posts.categories.desserts')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('posts.price')}
              </label>
              <input
                type="text"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={t('posts.pricePlaceholder')}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#a15507] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-3 text-sm font-medium text-gray-300">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="w-4 h-4 text-[#a15507] bg-gray-800 border border-gray-600 rounded focus:ring-[#a15507] focus:ring-2"
              />
              <span>{t('posts.featured')}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('posts.productPhotos', { count: images.length, max: 10 })}
            </label>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {images.map((image) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.preview}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {image.isExisting && (
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-500 text-white text-xs rounded">
                        {t('posts.existing')}
                      </div>
                    )}
                  </div>
                ))}
                {images.length < 10 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center hover:border-[#a15507] hover:bg-[#a15507]/10 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-400">{t('posts.addPhoto')}</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-600">
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <div className="flex items-center space-x-1">
                <Image className="w-4 h-4" />
                <span>{t('posts.photosCount', { count: images.length })}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Hash className="w-4 h-4" />
                <span>{t('posts.hashtagsSupported')}</span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!content.trim() || loading}
                className="px-6 py-2 bg-[#a15507] text-white rounded-lg hover:bg-[#924e06] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (editingPost ? t('posts.updating') : t('posts.posting')) : (editingPost ? t('common.update') : t('posts.post'))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedPostModal;
